"use client";

import { useState, useEffect } from "react";
import { useIMQA } from "@/components/imqa-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

export function ConfigForm() {
  const { config, updateConfig, initIMQA, deinitIMQA, isInitialized } = useIMQA();
  const { toast } = useToast();
  
  const [formValues, setFormValues] = useState({
    collectorUrl: '',
    serviceName: '',
    serviceVersion: '',
    serviceKey: '',
    serviceNamespace: '',
    deploymentEnvironment: '',
  });

  // Update form values when config changes
  useEffect(() => {
    setFormValues({
      collectorUrl: config.collectorUrl || '',
      serviceName: config.serviceName || '',
      serviceVersion: config.serviceVersion || '',
      serviceKey: config.serviceKey || '',
      serviceNamespace: config.serviceNamespace || '',
      deploymentEnvironment: config.deploymentEnvironment || '',
    });
  }, [config]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formValues.collectorUrl || !formValues.serviceName || !formValues.serviceVersion || !formValues.serviceKey) {
      toast({
        title: "Missing required fields",
        description: "All fields are required to initialize IMQA Web Agent",
        variant: "destructive",
      });
      return;
    }
    
    // Update config with form values
    updateConfig(formValues);
    
    toast({
      title: "Configuration saved",
      description: "IMQA Web Agent configuration has been saved to localStorage",
    });
  };

  const handleToggleAgent = () => {
    if (isInitialized) {
      deinitIMQA();
      toast({
        title: "IMQA Web Agent deactivated",
        description: "The agent has been stopped successfully",
      });
    } else {
      if (!formValues.collectorUrl || !formValues.serviceName || !formValues.serviceVersion || !formValues.serviceKey) {
        toast({
          title: "Cannot activate",
          description: "All configuration fields are required to initialize IMQA Web Agent",
          variant: "destructive",
        });
        return;
      }
      
      initIMQA();
      toast({
        title: "IMQA Web Agent activated",
        description: "The agent has been started successfully",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-2">
        <Label htmlFor="collectorUrl">Collector URL</Label>
        <Input
          id="collectorUrl"
          name="collectorUrl"
          placeholder="https://in-otel.imqa.io"
          value={formValues.collectorUrl}
          onChange={handleChange}
        />
      </div>
      
      <div className="grid gap-2">
        <Label htmlFor="serviceName">Service Name</Label>
        <Input
          id="serviceName"
          name="serviceName"
          placeholder="My Web Service"
          value={formValues.serviceName}
          onChange={handleChange}
        />
      </div>
      
      <div className="grid gap-2">
        <Label htmlFor="serviceVersion">Service Version</Label>
        <Input
          id="serviceVersion"
          name="serviceVersion"
          placeholder="1.0.0"
          value={formValues.serviceVersion}
          onChange={handleChange}
        />
      </div>
      
      <div className="grid gap-2">
        <Label htmlFor="serviceKey">Service Key</Label>
        <Input
          id="serviceKey"
          name="serviceKey"
          placeholder="Your IMQA service key"
          value={formValues.serviceKey}
          onChange={handleChange}
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="serviceNamespace">
          Service Namespace
          <span className="text-sm font-normal text-muted-foreground ml-2">(Optional)</span>
        </Label>
        <Input
          id="serviceNamespace"
          name="serviceNamespace"
          placeholder="e.g., my-org (Optional)"
          value={formValues.serviceNamespace}
          onChange={handleChange}
        />
        <p className="text-sm text-muted-foreground">
          Used to group related services together in the IMQA dashboard
        </p>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="deploymentEnvironment">
          Deployment Environment
          <span className="text-sm font-normal text-muted-foreground ml-2">(Optional)</span>
        </Label>
        <Input
          id="deploymentEnvironment"
          name="deploymentEnvironment"
          placeholder="e.g., development, staging, production (Optional)"
          value={formValues.deploymentEnvironment}
          onChange={handleChange}
        />
        <p className="text-sm text-muted-foreground">
          Identifies the environment where the application is deployed
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" className="flex-1 sm:flex-none">Save Configuration</Button>
        <Button
          type="button"
          variant={isInitialized ? "destructive" : "default"}
          onClick={handleToggleAgent}
          className="flex-1 sm:flex-none"
        >
          {isInitialized ? "Deactivate Agent" : "Activate Agent"}
        </Button>
      </div>
    </form>
  );
}